/**
 * Source identifier for the Flutter native-handshake "ready" signal (CXO-2903).
 * The native webview switches on `payload.source`, so this string is a contract
 * shared with the mobile app — keep it in sync with the Flutter handler.
 */
export const CHECKOUT_READY_SOURCE = "instore_checkout_loaded";

/** Action for the Flutter native-handshake "ready" signal (CXO-2903). */
export const CHECKOUT_READY_ACTION = "loaded";

/**
 * True when the native `Mobile` JS bridge is injected — i.e. we're running
 * inside the Tamara Flutter webview, not a plain browser. Flutter exposes this
 * via `addJavaScriptChannel('Mobile')` on both iOS and Android before page load.
 */
export const isMobileBridgeAvailable = (): boolean => {
  try {
    return !!Mobile;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    return false;
  }
};

/** Returns `true` only when the bridge was present and the post was attempted. */
const post = (message: Record<string, unknown>): boolean => {
  if (!isMobileBridgeAvailable()) {
    return false;
  }

  try {
    Mobile?.postMessage(JSON.stringify(message));
    return true;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (err) {
    console.error("Failed to send mobile message.");
    return false;
  }
};

export const sendMessage = (
  orderId: string,
  source: string,
  action: string,
) => {
  post({
    payload: {
      source,
      order_id: orderId,
    },
    action,
  });
};

/**
 * Tells the Flutter native webview that PPUI has fetched the current checkout
 * status and rendered the corresponding screen (CXO-2903). The mobile app uses
 * this to hand off from its native loading screen to the rendered web UI.
 *
 * Returns `true` when the message was posted to the bridge, `false` when the
 * bridge was unavailable (e.g. a plain browser) — used for observability.
 */
export const sendCheckoutReadyMessage = (params: {
  checkoutId: string;
  orderId: string;
  status: string;
}): boolean => {
  return post({
    payload: {
      source: CHECKOUT_READY_SOURCE,
      checkout_id: params.checkoutId,
      order_id: params.orderId,
      status: params.status,
    },
    action: CHECKOUT_READY_ACTION,
  });
};
